import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AdminAssessmentStorage,
  RespondentStoreProvider,
  useUserStore,
  generateFullReportHtml,
  type ApiService,
} from '@pro-groups/shared';
import { getPhiValue, PHI_FIELD, normalizeHistoryAdminResponse } from '@pro-groups/shared/types/api/patinfo';
import type { AppProfilePHIDto } from '@pro-groups/shared/types/api/appProfile';
import type { SecureLoginResponseDto } from '@pro-groups/shared/types/api/tokenAssessment';
import type { ShareReportGetRequestDto, ShareReportGetResponseDto } from '@pro-groups/shared/types/api/shareReport';
import { renderHtmlToPdfBlob } from '@pro-groups/shared/web';
import { webStorageAdapter } from '../storage/WebStorageAdapter';

interface Props {
  apiService: ApiService;
  token: string;
}

type ResolvedSession = SecureLoginResponseDto;

/** Converts the wire (PascalCase) PHI entries on ShareReportGetResponseDto into an AppProfilePHIDto. */
function buildPhiFromShareResponse(entries: ShareReportGetResponseDto['Phi']): AppProfilePHIDto {
  const phi = (entries ?? []).map((e) => ({ name: e.Name, value: e.Value }));
  const fname = getPhiValue(phi, PHI_FIELD.FNAME);
  const lname = getPhiValue(phi, PHI_FIELD.LNAME);
  return {
    dname: [fname, lname].filter(Boolean).join(' '),
    fname,
    lname,
    email: getPhiValue(phi, PHI_FIELD.EMAIL),
    dob: getPhiValue(phi, PHI_FIELD.DOB),
    gender: getPhiValue(phi, PHI_FIELD.GENDER),
    extra: [],
  };
}

/**
 * Entry point rendered by App.tsx for a matched /share token route (see authEntry.ts's
 * resolveTokenRoute). AccessType 43 is not an administration flow — it's a verify-then-view
 * flow for a report shared via secure email: resolve the token (which also triggers the
 * backend to email a verification code), collect that code, retrieve the report via the
 * 'share_report_get' sync RequestType, then render it straight to a PDF. No further
 * processing beyond that — no editing, no debug info, no re-share.
 */
export const ShareReportView: React.FC<Props> = ({ apiService, token }) => {
  const [resolved, setResolved] = useState<ResolvedSession | null>(null);
  const [resolveError, setResolveError] = useState<string | null>(null);
  // Same StrictMode-safe guard as SecureAdministrationView — secure-login for /share also
  // triggers a real backend side effect (emailing the verification code), so it must not
  // fire twice on React.StrictMode's intentional double-invoke.
  const resolveRequestRef = useRef<{ key: string; promise: Promise<Awaited<ReturnType<ApiService['resolveSecureAdministration']>>> } | null>(null);

  useEffect(() => {
    const requestKey = `share:${token}`;
    if (resolveRequestRef.current?.key !== requestKey) {
      resolveRequestRef.current = { key: requestKey, promise: apiService.resolveSecureAdministration('share', token) };
    }
    const { promise } = resolveRequestRef.current;

    let cancelled = false;
    setResolveError(null);
    void promise.then((result) => {
      if (cancelled) {
        return;
      }
      if (!result.success || !result.data) {
        setResolveError(result.message || 'Unable to resolve this shared report link.');
        return;
      }
      setResolved(result.data);
    });
    return () => {
      cancelled = true;
    };
  }, [apiService, token]);

  if (resolveError) {
    return <StatusScreen title="Link Error" message={resolveError} />;
  }

  if (!resolved) {
    return <StatusScreen title="Loading" message="Validating your shared report link…" />;
  }

  return (
    <RespondentStoreProvider storage={webStorageAdapter} resolved={resolved}>
      <ShareReportInner apiService={apiService} resolved={resolved} />
    </RespondentStoreProvider>
  );
};

const ShareReportInner: React.FC<{ apiService: ApiService; resolved: ResolvedSession }> = ({ apiService, resolved }) => {
  const userStoreHook = useUserStore();

  const [phase, setPhase] = useState<'verify' | 'verifying' | 'rendering' | 'viewing' | 'error'>('verify');
  const [verifyCode, setVerifyCode] = useState('');
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  // Revoke the object URL on unmount to avoid leaking it for the life of the tab.
  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  const handleVerify = useCallback(async () => {
    if (!verifyCode.trim()) {
      return;
    }
    setVerifyError(null);
    setPhase('verifying');
    try {
      const request: ShareReportGetRequestDto = {
        VerifyCode: verifyCode.trim(),
        // SecureLoginResponseDto.assessmentId doubles as the ShareReportId for /share sessions.
        ShareReportId: resolved.assessmentId,
      };
      const response = await apiService.processServiceRequestWithRefresh(
        { RequestType: 'share_report_get', Payload: JSON.stringify(request) },
        'sync',
        { userStore: userStoreHook, storage: webStorageAdapter }
      );

      if (!response.success) {
        setVerifyError(response.message || response.errorDescription || 'Incorrect verification code. Please try again.');
        setPhase('verify');
        return;
      }

      const shareReport: ShareReportGetResponseDto = JSON.parse(response.payload);
      setPhase('rendering');

      const assessmentStorage = new AdminAssessmentStorage(webStorageAdapter);
      let assessmentDefinition = await assessmentStorage.getAssessmentBySourceId(shareReport.AssessmentId);
      if (!assessmentDefinition) {
        const all = await assessmentStorage.getAvailableAssessments();
        assessmentDefinition = all.find((a) => a.surveyId === shareReport.AssessmentId) ?? null;
      }
      if (!assessmentDefinition || assessmentDefinition.reports.length === 0) {
        setPdfError(`Report content for "${shareReport.AssessmentId}" was not found.`);
        setPhase('error');
        return;
      }

      const selectedReport =
        assessmentDefinition.reports.find((r) => r.reportId === shareReport.ReportId) ?? assessmentDefinition.reports[0];
      const adminResponse = normalizeHistoryAdminResponse(shareReport.Data);
      const phi = buildPhiFromShareResponse(shareReport.Phi);

      const html = generateFullReportHtml(selectedReport, adminResponse, phi, {});
      const blob = await renderHtmlToPdfBlob(html);
      setPdfUrl(URL.createObjectURL(blob));
      setPhase('viewing');
    } catch (err) {
      console.error('ShareReportView: failed to retrieve/render shared report', err);
      setPdfError(err instanceof Error ? err.message : 'Something went wrong while loading this report.');
      setPhase('error');
    }
  }, [apiService, resolved.assessmentId, userStoreHook, verifyCode]);

  if (phase === 'error') {
    return <StatusScreen title="Unable to Show Report" message={pdfError ?? 'Something went wrong.'} />;
  }

  if (phase === 'rendering') {
    return <StatusScreen title="Loading" message="Preparing your report…" />;
  }

  if (phase === 'viewing' && pdfUrl) {
    return (
      <iframe
        src={pdfUrl}
        title="Shared Report"
        style={{ position: 'fixed', inset: 0, width: '100%', height: '100%', border: 'none' }}
      />
    );
  }

  return (
    <VerifyCodeScreen
      value={verifyCode}
      onChange={setVerifyCode}
      onSubmit={() => { void handleVerify(); }}
      error={verifyError}
      submitting={phase === 'verifying'}
    />
  );
};

const StatusScreen: React.FC<{ title: string; message: string }> = ({ title, message }) => (
  <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 20, background: '#eef2f7' }}>
    <div style={{ width: '100%', maxWidth: 560, background: '#fff', borderRadius: 16, padding: '28px 24px', boxShadow: '0 6px 24px rgba(0,0,0,0.1)' }}>
      <h1 style={{ margin: 0, fontSize: 24, color: '#1f2937' }}>{title}</h1>
      <p style={{ marginTop: 12, color: '#374151', fontSize: 14 }}>{message}</p>
    </div>
  </div>
);

const VerifyCodeScreen: React.FC<{
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  error: string | null;
  submitting: boolean;
}> = ({ value, onChange, onSubmit, error, submitting }) => (
  <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 20, background: '#eef2f7' }}>
    <div style={{ width: '100%', maxWidth: 420, background: '#fff', borderRadius: 16, padding: '28px 24px', boxShadow: '0 6px 24px rgba(0,0,0,0.1)' }}>
      <h1 style={{ margin: 0, fontSize: 22, color: '#1f2937' }}>Verify Your Email</h1>
      <p style={{ marginTop: 12, color: '#374151', fontSize: 14 }}>
        A verification code was sent to the email this report was shared with. Enter it below to view the report.
      </p>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Verification code"
        style={{
          width: '100%',
          marginTop: 16,
          padding: 12,
          fontSize: 16,
          borderRadius: 8,
          border: error ? '1px solid #e74c3c' : '1px solid #d1d5db',
          boxSizing: 'border-box',
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !submitting) {
            onSubmit();
          }
        }}
      />
      {error && <p style={{ marginTop: 8, color: '#e74c3c', fontSize: 13 }}>{error}</p>}
      <button
        onClick={onSubmit}
        disabled={submitting || !value.trim()}
        style={{
          marginTop: 16,
          width: '100%',
          padding: 14,
          fontSize: 16,
          fontWeight: 600,
          color: '#fff',
          background: '#10b981',
          border: 'none',
          borderRadius: 8,
          cursor: submitting ? 'default' : 'pointer',
          opacity: submitting || !value.trim() ? 0.6 : 1,
        }}
      >
        {submitting ? 'Verifying…' : 'View Report'}
      </button>
    </div>
  </div>
);
